// frontend/src/settings/AnnualHolidaySettings.js - Supabase対応版
import React, { useEffect, useState } from "react";
import { supabase, manageShiftPatterns, manageSkillGroups } from "../supabase";
import Header from "../components/ui/Header";
import { Link } from "react-router-dom";
import { useOrganization } from "../contexts/OrganizationContext";

function AnnualHolidaySettings() {
  const { currentOrganization } = useOrganization();
  const [annualTarget, setAnnualTarget] = useState(120);
  const [monthlyMin, setMonthlyMin] = useState(8);
  const [monthlyMax, setMonthlyMax] = useState(11);
  const [weeklyBaseline, setWeeklyBaseline] = useState(2.0);
  const [maxConsecutiveWorkDays, setMaxConsecutiveWorkDays] = useState(6.0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 休暇種別の集計設定（shift_patterns.configuration ベース）
  const [patterns, setPatterns] = useState([]);
  // pattern.id -> { isHolidayType, monthlyLimit, showInAggregation, showAcquisitionRate }
  const [holidayConfig, setHolidayConfig] = useState({});
  const [savingHolidays, setSavingHolidays] = useState(false);
  const [savedHolidays, setSavedHolidays] = useState(false);

  // 期間ベース上限のスキルグループ別設定で使用
  const [skillGroups, setSkillGroups] = useState([]);

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchSettings();
      fetchPatterns();
      fetchSkillGroups();
    }
  }, [currentOrganization]);

  const fetchSkillGroups = async () => {
    try {
      const data = await manageSkillGroups('list', { organizationId: currentOrganization.id });
      setSkillGroups((data || []).map(g => ({ id: g.id, name: g.name })));
    } catch (error) {
      console.error("スキルグループ取得に失敗しました:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const organizationId = currentOrganization?.id || 'default-hospital';

      const { data, error } = await supabase
        .from('holiday_policies')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        throw error;
      }

      if (data) {
        setAnnualTarget(data.annual_target || 120);
        setMonthlyMin(data.monthly_min || 8);
        setMonthlyMax(data.monthly_max || 11);
        setWeeklyBaseline(data.weekly_baseline || 2.0);
        setMaxConsecutiveWorkDays(data.max_consecutive_work_days || 6.0);
      }
    } catch (error) {
      console.error("設定の読み込みに失敗しました:", error);
      alert("設定の読み込みに失敗しました: " + error.message);
    }
  };

  const fetchPatterns = async () => {
    try {
      const data = await manageShiftPatterns('list', { organizationId: currentOrganization.id });
      // raw（manageShiftPatterns 展開後の全フィールド）をそのまま保持し、保存時に全部送り返す
      const list = (data || []).map((p) => {
        const mark = p.displayMark || p.short_name || '';
        const isDefaultHoliday = mark === '休';
        const byMonth = p.holiday_monthly_limit_by_month || null;
        const rawPeriods = Array.isArray(p.holiday_period_limits) ? p.holiday_period_limits : [];
        return {
          raw: p,
          id: p.id,
          name: p.name,
          displayMark: mark,
          priority: p.priority || 1,
          annualTotal: p.holiday_annual_total ?? null,
          monthlyLimit: p.holiday_monthly_limit ?? null,
          monthlyLimitByMonth: byMonth, // { "1": 8, "2": 7, ... } | null
          monthlyLimitMode: byMonth && Object.keys(byMonth).length > 0 ? 'detail' : 'simple',
          aggregationPeriod: p.aggregation_period || 'monthly',
          showInAggregation: p.show_in_aggregation ?? isDefaultHoliday,
          showAcquisitionRate: p.show_acquisition_rate ?? isDefaultHoliday,
          periodLimits: rawPeriods.map((pl) => ({
            id: pl.id || `pl_${Math.random().toString(36).slice(2, 10)}`,
            label: pl.label || '',
            startMonth: Number(pl.startMonth) || 1,
            endMonth: Number(pl.endMonth) || 12,
            maxCount: pl.maxCount != null ? Number(pl.maxCount) : null,
            // スキルグループ別の上限（任意）。 [{ skillGroupId, skillGroupName, maxCount }]
            maxCountByGroup: Array.isArray(pl.maxCountByGroup) ? pl.maxCountByGroup.map(g => ({
              skillGroupId: g.skillGroupId,
              skillGroupName: g.skillGroupName || '',
              maxCount: g.maxCount != null ? Number(g.maxCount) : null,
            })) : [],
          })),
        };
      }).sort((a, b) => (a.priority || 99) - (b.priority || 99));

      setPatterns(list);
      const config = {};
      list.forEach((p) => {
        config[p.id] = {
          annualTotal: p.annualTotal,
          monthlyLimit: p.monthlyLimit,
          monthlyLimitByMonth: p.monthlyLimitByMonth ? { ...p.monthlyLimitByMonth } : {},
          monthlyLimitMode: p.monthlyLimitMode,
          aggregationPeriod: p.aggregationPeriod || 'monthly',
          showInAggregation: !!p.showInAggregation,
          showAcquisitionRate: !!p.showAcquisitionRate,
          periodLimits: p.periodLimits.map((pl) => ({ ...pl })),
          showPeriodLimits: p.periodLimits.length > 0,
        };
      });
      setHolidayConfig(config);
    } catch (error) {
      console.error("勤務パターン取得に失敗しました:", error);
    }
  };

  // 期間制限の操作ヘルパー
  const addPeriodLimit = (patternId) => {
    setHolidayConfig((prev) => {
      const cur = prev[patternId] || {};
      const next = [...(cur.periodLimits || []), {
        id: `pl_${Math.random().toString(36).slice(2, 10)}`,
        label: '',
        startMonth: 1,
        endMonth: 12,
        maxCount: null,
        maxCountByGroup: [],
      }];
      return { ...prev, [patternId]: { ...cur, periodLimits: next, showPeriodLimits: true } };
    });
  };
  const updatePeriodLimit = (patternId, idx, field, value) => {
    setHolidayConfig((prev) => {
      const cur = prev[patternId] || {};
      const next = [...(cur.periodLimits || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, [patternId]: { ...cur, periodLimits: next } };
    });
  };
  const removePeriodLimit = (patternId, idx) => {
    setHolidayConfig((prev) => {
      const cur = prev[patternId] || {};
      const next = (cur.periodLimits || []).filter((_, i) => i !== idx);
      return { ...prev, [patternId]: { ...cur, periodLimits: next } };
    });
  };
  // periodLimit[idx].maxCountByGroup の操作ヘルパー
  const togglePeriodGroupOverride = (patternId, idx, skillGroupId, skillGroupName, defaultMaxCount) => {
    setHolidayConfig((prev) => {
      const cur = prev[patternId] || {};
      const limits = [...(cur.periodLimits || [])];
      const item = { ...limits[idx] };
      const byGroup = Array.isArray(item.maxCountByGroup) ? [...item.maxCountByGroup] : [];
      const existsIdx = byGroup.findIndex(g => g.skillGroupId === skillGroupId);
      if (existsIdx >= 0) {
        byGroup.splice(existsIdx, 1);
      } else {
        byGroup.push({
          skillGroupId,
          skillGroupName,
          maxCount: defaultMaxCount != null ? Number(defaultMaxCount) : null,
        });
      }
      item.maxCountByGroup = byGroup;
      limits[idx] = item;
      return { ...prev, [patternId]: { ...cur, periodLimits: limits } };
    });
  };
  const updatePeriodGroupMax = (patternId, idx, skillGroupId, maxCount) => {
    setHolidayConfig((prev) => {
      const cur = prev[patternId] || {};
      const limits = [...(cur.periodLimits || [])];
      const item = { ...limits[idx] };
      const byGroup = (item.maxCountByGroup || []).map(g =>
        g.skillGroupId === skillGroupId
          ? { ...g, maxCount: maxCount === '' || maxCount == null ? null : Number(maxCount) }
          : g
      );
      item.maxCountByGroup = byGroup;
      limits[idx] = item;
      return { ...prev, [patternId]: { ...cur, periodLimits: limits } };
    });
  };

  const updateHolidayField = (patternId, field, value) => {
    setHolidayConfig((prev) => ({
      ...prev,
      [patternId]: { ...prev[patternId], [field]: value },
    }));
  };

  // raw パターンから「保存時に保持するべき configuration キー」だけ抜き出す
  //   - 上位 (top-level) カラムは別途明示送信
  //   - manage-shift-patterns 'list' は raw.configuration を spread 済みなので、
  //     camelCase の上位キーと内部用キーを除外する
  const TOP_LEVEL_KEYS = new Set([
    'id', 'name', 'displayMark', 'shortName', 'startTime', 'endTime', 'color',
    'requiredCount', 'priority', 'displayOrder', 'isActive',
  ]);
  const extractConfigKeys = (raw) => {
    const out = {};
    Object.keys(raw || {}).forEach((k) => {
      if (!TOP_LEVEL_KEYS.has(k)) {
        out[k] = raw[k];
      }
    });
    return out;
  };

  const handleSaveHolidays = async () => {
    if (!currentOrganization?.id) return;
    setSavingHolidays(true);
    try {
      // 変更があったパターンのみ更新
      const monthlyByMonthEqual = (a, b) => {
        const ka = Object.keys(a || {}).filter((k) => a[k] != null);
        const kb = Object.keys(b || {}).filter((k) => b[k] != null);
        if (ka.length !== kb.length) return false;
        return ka.every((k) => Number(a[k]) === Number(b?.[k]));
      };
      const byGroupEqual = (a, b) => {
        const A = a || [];
        const B = b || [];
        if (A.length !== B.length) return false;
        const sortKey = (x) => String(x.skillGroupId);
        const A2 = [...A].sort((p, q) => sortKey(p).localeCompare(sortKey(q)));
        const B2 = [...B].sort((p, q) => sortKey(p).localeCompare(sortKey(q)));
        return A2.every((x, i) => {
          const y = B2[i];
          return String(x.skillGroupId) === String(y?.skillGroupId)
            && (x.maxCount ?? null) === (y?.maxCount ?? null);
        });
      };
      const periodLimitsEqual = (a, b) => {
        const A = a || [];
        const B = b || [];
        if (A.length !== B.length) return false;
        return A.every((x, i) => {
          const y = B[i];
          return (x.label || '') === (y.label || '')
            && Number(x.startMonth) === Number(y.startMonth)
            && Number(x.endMonth) === Number(y.endMonth)
            && (x.maxCount ?? null) === (y.maxCount ?? null)
            && byGroupEqual(x.maxCountByGroup, y.maxCountByGroup);
        });
      };
      const updates = patterns
        .filter((p) => {
          const c = holidayConfig[p.id];
          if (!c) return false;
          return (
            (c.annualTotal ?? null) !== (p.annualTotal ?? null)
            || (c.monthlyLimit ?? null) !== (p.monthlyLimit ?? null)
            || (c.monthlyLimitMode || 'simple') !== (p.monthlyLimitMode || 'simple')
            || !monthlyByMonthEqual(c.monthlyLimitByMonth, p.monthlyLimitByMonth)
            || (c.aggregationPeriod || 'monthly') !== (p.aggregationPeriod || 'monthly')
            || c.showInAggregation !== !!p.showInAggregation
            || c.showAcquisitionRate !== !!p.showAcquisitionRate
            || !periodLimitsEqual(c.periodLimits, p.periodLimits)
          );
        })
        .map((p) => ({ pattern: p, config: holidayConfig[p.id] }));

      for (const { pattern, config } of updates) {
        const raw = pattern.raw || {};
        // 1) 元の設定を全部保持
        // 2) 上位カラムは raw のキャメルケースから取り出して snake_case で送信
        // 3) 集計欄関連だけ最後に上書き
        await manageShiftPatterns('update', {
          patternId: pattern.id,
          organizationId: currentOrganization.id,
          // 上位カラム（list が camelCase で返すので拾い直す）
          name: raw.name,
          display_mark: raw.displayMark || raw.shortName,
          start_time: raw.startTime,
          end_time: raw.endTime,
          color: raw.color,
          required_count: raw.requiredCount,
          priority: raw.priority,
          // 残りの configuration キー（snake_case）を全部保持
          ...extractConfigKeys(raw),
          // 集計欄表示の最終上書き
          holiday_annual_total:
            config.annualTotal === '' || config.annualTotal == null
              ? null
              : Number(config.annualTotal),
          holiday_monthly_limit:
            config.monthlyLimit === '' || config.monthlyLimit == null
              ? null
              : Number(config.monthlyLimit),
          // 月毎の詳細設定（detail モードかつ少なくとも 1 月でも値があれば保存）
          holiday_monthly_limit_by_month:
            (config.monthlyLimitMode === 'detail' && config.monthlyLimitByMonth)
              ? (() => {
                  const cleaned = {};
                  for (let m = 1; m <= 12; m++) {
                    const v = config.monthlyLimitByMonth[m];
                    if (v != null && v !== '') cleaned[m] = Number(v);
                  }
                  return Object.keys(cleaned).length > 0 ? cleaned : null;
                })()
              : null,
          aggregation_period: config.aggregationPeriod || 'monthly',
          show_in_aggregation: !!config.showInAggregation,
          show_acquisition_rate: !!config.showAcquisitionRate,
          holiday_period_limits: (() => {
            const list = (config.periodLimits || [])
              .filter((pl) => pl.maxCount != null && pl.maxCount !== '' && Number(pl.maxCount) > 0)
              .map((pl) => ({
                id: pl.id,
                label: (pl.label || '').trim(),
                startMonth: Number(pl.startMonth) || 1,
                endMonth: Number(pl.endMonth) || 12,
                maxCount: Number(pl.maxCount),
                maxCountByGroup: Array.isArray(pl.maxCountByGroup)
                  ? pl.maxCountByGroup
                      .filter(g => g.skillGroupId && g.maxCount != null && g.maxCount !== '')
                      .map(g => ({
                        skillGroupId: g.skillGroupId,
                        skillGroupName: g.skillGroupName || '',
                        maxCount: Number(g.maxCount),
                      }))
                  : [],
              }));
            return list.length > 0 ? list : null;
          })(),
        });
      }

      await fetchPatterns();
      setSavedHolidays(true);
      setTimeout(() => setSavedHolidays(false), 3000);
    } catch (error) {
      console.error("集計設定の保存に失敗しました:", error);
      alert("集計設定の保存に失敗しました: " + error.message);
    } finally {
      setSavingHolidays(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const organizationId = currentOrganization?.id || 'default-hospital';

      const { error } = await supabase
        .from('holiday_policies')
        .upsert({
          organization_id: organizationId,
          annual_target: annualTarget,
          monthly_min: monthlyMin,
          monthly_max: monthlyMax,
          weekly_baseline: weeklyBaseline,
          max_consecutive_work_days: maxConsecutiveWorkDays,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organization_id'
        });

      if (error) throw error;

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error("設定の保存に失敗しました:", error);
      alert("設定の保存に失敗しました: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  // 公休（mark='休'）のデフォルト月間上限：年間目標 / 12 を四捨五入
  const defaultMonthlyHoliday = Math.round((annualTarget || 0) / 12);

  // スタイル定義
  const styles = {
    container: {
      padding: "2rem",
      maxWidth: "800px",
      margin: "0 auto",
      fontFamily: "sans-serif"
    },
    backLink: {
      display: "inline-block",
      marginBottom: "1rem",
      color: "#007bff",
      textDecoration: "none"
    },
    card: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      padding: "2rem",
      marginTop: "1.5rem",
      border: "1px solid #e0e0e0"
    },
    header: {
      borderBottom: "1px solid #eee",
      paddingBottom: "1rem",
      marginBottom: "1.5rem"
    },
    formGroup: {
      marginBottom: "1.5rem"
    },
    label: {
      display: "block",
      marginBottom: "0.5rem",
      fontWeight: "500",
      color: "#333"
    },
    input: {
      width: "100%",
      padding: "0.6rem",
      borderRadius: "4px",
      border: "1px solid #ccc",
      fontSize: "1rem"
    },
    select: {
      width: "100%",
      padding: "0.6rem",
      borderRadius: "4px",
      border: "1px solid #ccc",
      fontSize: "1rem",
      backgroundColor: "#fff"
    },
    helpText: {
      fontSize: "0.85rem",
      color: "#666",
      marginTop: "0.4rem"
    },
    buttonContainer: {
      marginTop: "2rem",
      display: "flex",
      justifyContent: "flex-end"
    },
    button: {
      padding: "0.75rem 1.5rem",
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "4px",
      fontSize: "1rem",
      cursor: "pointer",
      transition: "background-color 0.2s"
    },
    buttonLoading: {
      backgroundColor: "#6c757d",
      cursor: "not-allowed"
    },
    buttonSuccess: {
      backgroundColor: "#28a745"
    },
    infoBox: {
      backgroundColor: "#e7f3ff",
      padding: "1rem",
      borderRadius: "8px",
      marginBottom: "1.5rem",
      border: "1px solid #b8daff"
    },
    infoTitle: {
      marginTop: 0,
      color: "#004085",
      fontSize: "1.1rem"
    },
    infoText: {
      color: "#004085",
      marginBottom: 0
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "1rem",
    },
    tableHeaderRow: {
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #dee2e6",
    },
    tableHeaderCell: {
      padding: "0.6rem 0.75rem",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "0.9rem",
      color: "#333",
    },
    tableRow: {
      borderBottom: "1px solid #f1f3f4",
    },
    tableCell: {
      padding: "0.6rem 0.75rem",
      verticalAlign: "middle",
    },
    tableCellCenter: {
      padding: "0.6rem 0.75rem",
      textAlign: "center",
      verticalAlign: "middle",
    },
    markBadge: {
      display: "inline-block",
      backgroundColor: "#f1f5f9",
      color: "#475569",
      padding: "0.1rem 0.5rem",
      borderRadius: "4px",
      marginRight: "0.5rem",
      fontWeight: 700,
      fontSize: "0.9rem",
    },
    publicHolidayTag: {
      display: "inline-block",
      backgroundColor: "#dcfce7",
      color: "#15803d",
      padding: "0.1rem 0.4rem",
      borderRadius: "4px",
      fontSize: "0.75rem",
      fontWeight: 700,
      marginLeft: "0.5rem",
    },
  };

  if (!currentOrganization?.id) {
    return (
      <div style={styles.container}>
        <Header />
        <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
        <div style={styles.card}>
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            組織を選択してください
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />
      
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
      
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>年間休日設定</h2>
        </div>
        
        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>⚠️ このセクションの役割</h3>
          <p style={styles.infoText}>
            ここは <strong>公休（マーク「休」）のポリシー</strong> 設定です。
            シフト自動割り当ての休日バランス調整に使われます。
            <br />
            「夏休み」「育児休暇」など <strong>独自の休暇種別</strong> や、集計欄への表示有無は
            このページ下の <strong>「勤務パターンごとの集計設定」</strong> で個別に設定してください。
          </p>
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>年間休日目標</label>
          <input
            type="number"
            value={annualTarget}
            onChange={(e) => setAnnualTarget(Number(e.target.value))}
            style={styles.input}
            min="1"
            max="365"
          />
          <p style={styles.helpText}>
            全スタッフが年間に取得すべき休日の合計目標日数を設定します。
            通常は法定休日、夏季休暇、年末年始休暇などを含めた日数を設定します。
          </p>
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>月間最低休日数</label>
          <input
            type="number"
            value={monthlyMin}
            onChange={(e) => setMonthlyMin(Number(e.target.value))}
            style={styles.input}
            min="0"
            max="31"
          />
          <p style={styles.helpText}>
            スタッフが1ヶ月間に最低限取得すべき休日数を設定します。
            シフト自動割り当て時にこの日数を下回らないように調整されます。
          </p>
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>月間最大休日数</label>
          <input
            type="number"
            value={monthlyMax}
            onChange={(e) => setMonthlyMax(Number(e.target.value))}
            style={styles.input}
            min="0"
            max="31"
          />
          <p style={styles.helpText}>
            スタッフが1ヶ月間に取得できる休日の上限を設定します。
            特定の月に休日が集中しすぎないように調整するために使用されます。
          </p>
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>週休基準（日数）</label>
          <select
            value={weeklyBaseline}
            onChange={(e) => setWeeklyBaseline(parseFloat(e.target.value))}
            style={styles.select}
          >
            <option value={0.5}>0.5日/週</option>
            <option value={1}>1.0日/週</option>
            <option value={1.5}>1.5日/週</option>
            <option value={2}>2.0日/週</option>
            <option value={2.5}>2.5日/週</option>
            <option value={3}>3.0日/週</option>
          </select>
          <p style={styles.helpText}>
            通常の週単位での休日目標を設定します。一般的には週休2日制の場合は2.0を選択します。
            シフト自動生成時の休日配置バランスに影響します。
          </p>
        </div>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>最大連続勤務日数</label>
          <input
            type="number"
            value={maxConsecutiveWorkDays}
            onChange={(e) => setMaxConsecutiveWorkDays(Number(e.target.value))}
            style={styles.input}
            min="0.5"
            max="31"
            step="0.5"
          />
          <p style={styles.helpText}>
            スタッフが連続して勤務できる最大日数を設定します。半日勤務も考慮されるため、
            0.5日単位で設定できます（例：5.5日 = 5日半の連続勤務）。
            シフト自動割り当て時に、この日数を超えないように休日が配置されます。
          </p>
        </div>
        
        <div style={styles.buttonContainer}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.button,
              ...(saving ? styles.buttonLoading : {}),
              ...(saved ? styles.buttonSuccess : {})
            }}
          >
            {saving ? "保存中..." : saved ? "✓ 保存しました" : "設定を保存"}
          </button>
        </div>
      </div>

      {/* 勤務パターンごとの集計設定 */}
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>勤務パターンごとの集計設定</h2>
        </div>

        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>📊 ここで決まること</h3>
          <p style={styles.infoText}>
            シフト作成画面のスタッフ集計欄に、どの勤務パターンの取得数を表示するかを決めます。
          </p>
          <ul style={{ margin: "0.6rem 0 0 1.2rem", color: "#004085", fontSize: "0.92rem" }}>
            <li>「集計欄に表示」を ON にした勤務パターンが集計バッジとして並びます</li>
            <li>「集計期間」が <strong>月間</strong> の場合: <u>当月の取得数 / 月間取得上限</u>（例: 公休 9/10）</li>
            <li>「集計期間」が <strong>年度累計</strong> の場合: <u>当年度（4月〜表示月）の累計取得数 / 年間総日数</u>（例: 夏休み 4/10）。<br />年度は <strong>毎年4月始まり、翌年3月締め</strong> です。</li>
            <li>「取得率を表示」を ON にするとパーセント表示も付きます</li>
          </ul>
        </div>

        <div style={{
          background: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: "8px",
          padding: "0.9rem 1rem",
          marginBottom: "1.5rem",
          fontSize: "0.92rem",
          color: "#92400e",
        }}>
          <strong>📌 公休（マーク「休」）の使い分け：</strong>
          <ul style={{ margin: "0.4rem 0 0 1.2rem" }}>
            <li><strong>「年間休日設定」(上のセクション)</strong> は<u>組織全体のポリシー</u>。シフト自動割当のアルゴリズムで使われます。</li>
            <li><strong>「年間総日数 / 月間取得上限」(下のテーブル)</strong> は<u>集計表示専用</u>。
              公休行を空欄のままにすると、上の「年間休日目標 = {annualTarget}日」から自動的に
              <strong> 年間 {annualTarget}日 / 月間 {defaultMonthlyHoliday}日</strong> が既定値として使われます。
            </li>
            <li>夏休みなど施設独自の休みは、ここで明示的に「年間総日数」と「月間取得上限」を入れてください。</li>
          </ul>
        </div>

        {patterns.length === 0 ? (
          <div style={{ padding: "1.5rem", color: "#666", textAlign: "center" }}>
            勤務パターンがまだ登録されていません。先に「勤務パターン設定」で登録してください。
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.tableHeaderCell}>パターン</th>
                  <th style={styles.tableHeaderCell}>集計欄に表示</th>
                  <th style={styles.tableHeaderCell}>年間総日数</th>
                  <th style={styles.tableHeaderCell}>月間取得上限</th>
                  <th style={styles.tableHeaderCell}>取得率の集計期間</th>
                  <th style={styles.tableHeaderCell}>取得率を表示</th>
                </tr>
              </thead>
              <tbody>
                {patterns.map((p) => {
                  const c = holidayConfig[p.id] || {};
                  const isPublicHoliday = p.displayMark === '休';
                  return (
                    <React.Fragment key={p.id}>
                    <tr style={styles.tableRow}>
                      <td style={styles.tableCell}>
                        <div style={{ fontWeight: 600 }}>
                          <span style={styles.markBadge}>{p.displayMark || '?'}</span>
                          {p.name}
                          {isPublicHoliday && (
                            <span style={styles.publicHolidayTag}>公休</span>
                          )}
                        </div>
                      </td>
                      <td style={styles.tableCellCenter}>
                        <input
                          type="checkbox"
                          checked={!!c.showInAggregation}
                          onChange={(e) => updateHolidayField(p.id, 'showInAggregation', e.target.checked)}
                        />
                      </td>
                      <td style={styles.tableCellCenter}>
                        <input
                          type="number"
                          value={c.annualTotal ?? ''}
                          onChange={(e) =>
                            updateHolidayField(
                              p.id,
                              'annualTotal',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                          placeholder={isPublicHoliday ? String(annualTarget) : '任意'}
                          min="0"
                          max="365"
                          disabled={!c.showInAggregation}
                          style={{
                            ...styles.input,
                            width: "80px",
                            padding: "0.35rem 0.5rem",
                            opacity: c.showInAggregation ? 1 : 0.5,
                          }}
                        />
                        <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '0.3rem' }}>日/年</span>
                      </td>
                      <td style={styles.tableCellCenter}>
                        {c.monthlyLimitMode === 'detail' ? (
                          <span style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: 600 }}>
                            月毎に設定中
                          </span>
                        ) : (
                          <>
                            <input
                              type="number"
                              value={c.monthlyLimit ?? ''}
                              onChange={(e) =>
                                updateHolidayField(
                                  p.id,
                                  'monthlyLimit',
                                  e.target.value === '' ? null : Number(e.target.value)
                                )
                              }
                              placeholder={isPublicHoliday ? String(defaultMonthlyHoliday) : '任意'}
                              min="0"
                              max="31"
                              disabled={!c.showInAggregation}
                              style={{
                                ...styles.input,
                                width: "80px",
                                padding: "0.35rem 0.5rem",
                                opacity: c.showInAggregation ? 1 : 0.5,
                              }}
                            />
                            <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '0.3rem' }}>日/月</span>
                          </>
                        )}
                        <div style={{ marginTop: '0.4rem' }}>
                          <button
                            type="button"
                            onClick={() =>
                              updateHolidayField(
                                p.id,
                                'monthlyLimitMode',
                                c.monthlyLimitMode === 'detail' ? 'simple' : 'detail'
                              )
                            }
                            disabled={!c.showInAggregation}
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.2rem 0.5rem',
                              backgroundColor: c.monthlyLimitMode === 'detail' ? '#0ea5e9' : '#f1f5f9',
                              color: c.monthlyLimitMode === 'detail' ? 'white' : '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '4px',
                              cursor: c.showInAggregation ? 'pointer' : 'not-allowed',
                            }}
                          >
                            📅 月毎に詳細設定 {c.monthlyLimitMode === 'detail' ? '(ON)' : ''}
                          </button>
                        </div>
                      </td>
                      <td style={styles.tableCellCenter}>
                        <select
                          value={c.aggregationPeriod || 'monthly'}
                          onChange={(e) => updateHolidayField(p.id, 'aggregationPeriod', e.target.value)}
                          disabled={!c.showInAggregation}
                          style={{
                            ...styles.input,
                            width: "100px",
                            padding: "0.35rem 0.5rem",
                            opacity: c.showInAggregation ? 1 : 0.5,
                          }}
                          title={
                            c.aggregationPeriod === 'annual'
                              ? '当年度（4月〜表示月）累計を年間総日数と比較（例: 4/10）'
                              : '当月取得数を月間取得上限と比較（例: 9/10）'
                          }
                        >
                          <option value="monthly">月間（例: 9/10）</option>
                          <option value="annual">年度累計（4月〜、例: 4/10）</option>
                        </select>
                      </td>
                      <td style={styles.tableCellCenter}>
                        <input
                          type="checkbox"
                          checked={!!c.showAcquisitionRate}
                          onChange={(e) => updateHolidayField(p.id, 'showAcquisitionRate', e.target.checked)}
                          disabled={!c.showInAggregation}
                        />
                        <div style={{ marginTop: '0.4rem' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !c.showPeriodLimits;
                              updateHolidayField(p.id, 'showPeriodLimits', next);
                              if (next && (!c.periodLimits || c.periodLimits.length === 0)) {
                                addPeriodLimit(p.id);
                              }
                            }}
                            style={{
                              fontSize: '0.75rem',
                              padding: '0.2rem 0.5rem',
                              backgroundColor: c.showPeriodLimits || (c.periodLimits?.length > 0) ? '#a855f7' : '#f1f5f9',
                              color: c.showPeriodLimits || (c.periodLimits?.length > 0) ? 'white' : '#475569',
                              border: '1px solid #cbd5e1',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                            title="例: 夏期 7〜9月で5回、年末年始 12〜2月で3回 など"
                          >
                            🗓 期間で上限{(c.periodLimits?.length > 0) ? ` (${c.periodLimits.length})` : ''}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* 期間ベース上限設定の展開行 */}
                    {(c.showPeriodLimits || (c.periodLimits?.length > 0)) && (
                      <tr style={{ backgroundColor: '#faf5ff' }}>
                        <td colSpan={6} style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ fontSize: '0.85rem', color: '#581c87', fontWeight: 600, marginBottom: '0.5rem' }}>
                            「{p.name}（{p.displayMark}）」の期間ベース取得上限（毎年繰り返し）
                            <span style={{ marginLeft: '0.5rem', color: '#64748b', fontWeight: 400 }}>
                              例: 夏期 7〜9月で5回、年末年始 12〜2月で3回。期間外はシフト提出フォームに出ません。
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {(c.periodLimits || []).map((pl, idx) => {
                              const isYearCross = Number(pl.endMonth) < (Number(pl.startMonth));
                              const byGroup = Array.isArray(pl.maxCountByGroup) ? pl.maxCountByGroup : [];
                              const hasGroupOverride = byGroup.length > 0;
                              return (
                                <div key={pl.id} style={{
                                  padding: '0.6rem 0.75rem',
                                  background: 'white',
                                  border: '1px solid #e9d5ff',
                                  borderRadius: '6px',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                  }}>
                                    <input
                                      type="text"
                                      value={pl.label}
                                      onChange={(e) => updatePeriodLimit(p.id, idx, 'label', e.target.value)}
                                      placeholder="例: 夏期休暇 / 年末年始"
                                      style={{
                                        padding: '0.35rem 0.55rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        minWidth: '160px',
                                        fontSize: '0.85rem',
                                      }}
                                    />
                                    <select
                                      value={pl.startMonth}
                                      onChange={(e) => updatePeriodLimit(p.id, idx, 'startMonth', Number(e.target.value))}
                                      style={{ padding: '0.3rem 0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                                    >
                                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m}月</option>)}
                                    </select>
                                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>〜</span>
                                    <select
                                      value={pl.endMonth}
                                      onChange={(e) => updatePeriodLimit(p.id, idx, 'endMonth', Number(e.target.value))}
                                      style={{ padding: '0.3rem 0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                                    >
                                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m}月</option>)}
                                    </select>
                                    {isYearCross && (
                                      <span style={{ fontSize: '0.7rem', color: '#9333ea', background: '#f3e8ff', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                                        年跨ぎ
                                      </span>
                                    )}
                                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>合計</span>
                                    <input
                                      type="number"
                                      min="1"
                                      max="99"
                                      value={pl.maxCount ?? ''}
                                      onChange={(e) => updatePeriodLimit(p.id, idx, 'maxCount', e.target.value === '' ? null : Number(e.target.value))}
                                      placeholder="回数"
                                      style={{
                                        width: '70px',
                                        padding: '0.35rem 0.5rem',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                      }}
                                    />
                                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>回まで</span>
                                    <button
                                      type="button"
                                      onClick={() => removePeriodLimit(p.id, idx)}
                                      style={{
                                        marginLeft: 'auto',
                                        padding: '0.25rem 0.55rem',
                                        background: '#fee2e2',
                                        color: '#b91c1c',
                                        border: '1px solid #fecaca',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      削除
                                    </button>
                                  </div>

                                  {/* スキルグループ別の上限 */}
                                  <div style={{ marginTop: '0.6rem', borderTop: '1px dashed #e9d5ff', paddingTop: '0.6rem' }}>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#6b21a8', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={hasGroupOverride}
                                        onChange={(e) => {
                                          if (!e.target.checked) {
                                            updatePeriodLimit(p.id, idx, 'maxCountByGroup', []);
                                          } else if (skillGroups.length > 0) {
                                            // チェックを ON にしたら、最初のグループを既定値で追加
                                            const first = skillGroups[0];
                                            updatePeriodLimit(p.id, idx, 'maxCountByGroup', [{
                                              skillGroupId: first.id,
                                              skillGroupName: first.name,
                                              maxCount: pl.maxCount ?? null,
                                            }]);
                                          }
                                        }}
                                      />
                                      スキルグループごとに異なる上限を設定
                                    </label>
                                    {hasGroupOverride && (
                                      <div style={{ marginTop: '0.5rem', padding: '0.6rem', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '6px' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#7c3aed', marginBottom: '0.5rem' }}>
                                          💡 ここで指定したグループに属するスタッフには、そのグループに割り当てた上限が適用されます。<br />
                                          選択していないグループや、どのグループにも属さないスタッフには、上の基本上限（<strong>{pl.maxCount ?? '未設定'}</strong> 回）が適用されます。<br />
                                          <span style={{ color: '#a78bfa' }}>※万一複数の選択済みグループに属するスタッフがいた場合は、最も厳しい（小さい）値が適用されます。</span>
                                        </div>
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                          gap: '0.4rem',
                                          marginBottom: '0.5rem',
                                        }}>
                                          {skillGroups.map(group => {
                                            const isSel = byGroup.some(b => b.skillGroupId === group.id);
                                            return (
                                              <label key={group.id} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                padding: '0.35rem 0.5rem',
                                                background: isSel ? '#ede9fe' : 'white',
                                                border: isSel ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                fontSize: '0.8rem',
                                              }}>
                                                <input
                                                  type="checkbox"
                                                  checked={isSel}
                                                  onChange={() => togglePeriodGroupOverride(p.id, idx, group.id, group.name, pl.maxCount)}
                                                />
                                                <span style={{ fontWeight: isSel ? 600 : 400 }}>{group.name}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                        {byGroup.length > 0 && byGroup.map(g => (
                                          <div key={g.skillGroupId} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.6rem',
                                            padding: '0.4rem 0.6rem',
                                            background: 'white',
                                            border: '1px solid #ddd6fe',
                                            borderRadius: '4px',
                                            marginBottom: '0.3rem',
                                            fontSize: '0.85rem',
                                          }}>
                                            <span style={{ fontWeight: 500, minWidth: '120px' }}>{g.skillGroupName}</span>
                                            <input
                                              type="number"
                                              min="0"
                                              max="99"
                                              value={g.maxCount ?? ''}
                                              onChange={(e) => updatePeriodGroupMax(p.id, idx, g.skillGroupId, e.target.value)}
                                              placeholder="回数"
                                              style={{ width: '70px', padding: '0.3rem 0.45rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', textAlign: 'center' }}
                                            />
                                            <span style={{ color: '#64748b' }}>回まで</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => addPeriodLimit(p.id)}
                              style={{
                                alignSelf: 'flex-start',
                                padding: '0.35rem 0.75rem',
                                background: '#f3e8ff',
                                color: '#7e22ce',
                                border: '1px dashed #c084fc',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                              }}
                            >
                              + 期間を追加
                            </button>
                          </div>
                          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '4px', fontSize: '0.75rem', color: '#854d0e' }}>
                            💡 ここで期間が設定されたパターンは、シフト提出フォームで <strong>該当する月だけ表示</strong>され、上限に達したら追加できなくなります。取得済み回数（過去シフトから算出）も「3/5取得」のように可視化されます。
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* 月毎詳細設定の展開行 */}
                    {c.monthlyLimitMode === 'detail' && c.showInAggregation && (
                      <tr style={{ backgroundColor: '#f0f9ff' }}>
                        <td colSpan={6} style={{ padding: '0.6rem 1rem' }}>
                          <div style={{ fontSize: '0.85rem', color: '#0c4a6e', fontWeight: 600, marginBottom: '0.5rem' }}>
                            「{p.name}（{p.displayMark}）」の月毎の取得上限
                            <span style={{ marginLeft: '0.5rem', color: '#64748b', fontWeight: 400 }}>
                              （空欄の月は「日/月」の値が適用、両方空なら制限なし）
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.4rem' }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                              <label
                                key={m}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                  fontSize: '0.85rem',
                                }}
                              >
                                <span style={{ width: '2.5rem', color: '#475569' }}>{m}月</span>
                                <input
                                  type="number"
                                  value={c.monthlyLimitByMonth?.[m] ?? ''}
                                  onChange={(e) => {
                                    const nextVal = e.target.value === '' ? null : Number(e.target.value);
                                    updateHolidayField(p.id, 'monthlyLimitByMonth', {
                                      ...(c.monthlyLimitByMonth || {}),
                                      [m]: nextVal,
                                    });
                                  }}
                                  placeholder={
                                    c.monthlyLimit != null
                                      ? String(c.monthlyLimit)
                                      : isPublicHoliday
                                        ? String(defaultMonthlyHoliday)
                                        : ''
                                  }
                                  min="0"
                                  max="31"
                                  style={{
                                    width: '4rem',
                                    padding: '0.25rem 0.4rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem',
                                  }}
                                />
                                <span style={{ color: '#64748b', fontSize: '0.75rem' }}>日</span>
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={styles.buttonContainer}>
          <button
            onClick={handleSaveHolidays}
            disabled={savingHolidays}
            style={{
              ...styles.button,
              ...(savingHolidays ? styles.buttonLoading : {}),
              ...(savedHolidays ? styles.buttonSuccess : {})
            }}
          >
            {savingHolidays ? "保存中..." : savedHolidays ? "✓ 保存しました" : "集計設定を保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnnualHolidaySettings;